import * as React from "react";
import type { RowValue } from "../../../../../../lib/csv";
import parseCSV from "../../../../../../lib/csv";
import { isExpectedFormat } from "../../../../../../lib/fileHelper";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../../../i18n";
import Dropzone from "@ui/Dropzone";
import ModalForm from "@ui/Modal/Form";
import Select from "@ui/Select";
import { useQuantities } from "../../../../../../api/useQuantities";
import validateImport from "./validateImport";
import type { FileKeys, TypeModel, MapFile } from "./FilesType";
import { FileType, isModelType, CSVColumnMapping, ErrorMessage } from "./FilesType";
import ModalInfos from "./ModalInfos";
import type { Project } from "../../../../../../api/useProjects";
import type { Setup } from "../../../../../../api/useSetups";
import { usePost } from "../../../../../../api/useCall";
import type { Input } from "../../../../../../api/call";
import { useSettings } from "../../../../../../api/useSettings";
import { getCorpusGraph } from "diagnostics-lib/runtime";

export const modelTypes = [
  { label: "dymola2012_exe_32bits", value: "DYMOLA" },
  { label: "FMU_T0_INJECTION", value: "FMU_T0" },
  { label: "FMU_Tf_INJECTION", value: "FMU_TF" },
];

interface P {
  project: Project;
  setup: Setup;
  onRequestClose: () => void;
}

export type InputBinding = Input<
  "POST",
  "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/inputs"
>["body"]["inputs"];

export const buildInputBinding = (line: RowValue, quantityId: string) => {
  if (line["inputModelName"]) {
    const input: InputBinding[number] = {
      quantityId,
      modelName: line["inputModelName"],
    };
    if (line["orderExternalGap"] !== null && line["referenceValue"] !== null) {
      input["externalGap"] = {
        order: Number(line["orderExternalGap"]),
        reference: Number(line["referenceValue"]),
      };
    }
    return input;
  }
  return null;
};

const ImportModelModalForm = ({ project, setup, onRequestClose }: P) => {
  const projectId = project.id;

  const settings = useSettings();
  const modelType = useInput(FileType.fmuTf as TypeModel);
  const modelTypesAllowed = React.useMemo(() => {
    if (settings.ALLOW_USE_DYMOSIM_AND_FMU_T0) {
      return modelTypes;
    }
    return modelTypes.filter(model => model.value === FileType.fmuTf);
  }, [settings]);

  const [submitted, setSubmitted] = React.useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<{
    files: File[];
    validations: Map<FileKeys, MapFile>;
  }>({
    files: [],
    validations: new Map(),
  });
  const [valid, setValid] = React.useState<boolean | null>(false);
  const quantities = useQuantities(projectId);
  const quantityNames = quantities.map(quantity => quantity.name);

  const { addToast } = useToasts();

  const postModel = usePost("/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/model");
  const postInputs = usePost("/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/inputs");
  const postOutputs = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/outputs",
  );

  const resetState = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      modelType.onChange(event);
      setUploadedFiles({
        files: [],
        validations: new Map(),
      });
      setSubmitted(false);
    },
    [modelType],
  );

  const corpus = React.useMemo(
    () =>
      quantities.reduce<Record<string, string | null>>(
        (acc, { name, formula }) => ((acc[name] = formula), acc),
        {},
      ),
    [quantities],
  );
  const graph = React.useMemo(() => {
    let valid, graph;
    try {
      ({ graph, valid } = getCorpusGraph(corpus));
    } catch (e) {
      return null;
    }

    return valid ? (graph as { [token: string]: string[] | null }) : null;
  }, [corpus]);
  const expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  } = React.useMemo(() => ({ corpus, graph }), [corpus, graph]);

  const handleDrop = React.useCallback(
    async (files: File[]) => {
      setUploadedFiles({ ...uploadedFiles, ...{ files } });

      const validations = await validateImport(
        files,
        quantityNames,
        modelType.value,
        expressionEngineInput,
      );
      const { value: modelTypeValue } = modelType;
      const isFileOk = (fileType: FileKeys) =>
        validations.get(fileType)?.fileName !== undefined &&
        validations.get(fileType)?.error === undefined;

      let requiredFilesOk;
      if (modelTypeValue === FileType.dymola) {
        requiredFilesOk = isFileOk("main") && isFileOk("variables");
      } else if (modelTypeValue === FileType.fmuTf || modelTypeValue === FileType.fmuTo) {
        requiredFilesOk = isFileOk("main");
      } else {
        throw new Error(`Invalid model type: ${modelTypeValue}`);
      }

      let checkRequiredBinding = true;
      if (
        validations
          .get("binding")
          ?.error?.filter(e => e.message === ErrorMessage.CannotDependOnPastFunction)
      )
        checkRequiredBinding = false;

      setValid(requiredFilesOk && checkRequiredBinding);
      setSubmitted(true);
      setUploadedFiles({ ...uploadedFiles, ...{ files, validations } });
    },
    [modelType, quantityNames, uploadedFiles, expressionEngineInput],
  );

  const handleSubmit = React.useCallback(async () => {
    const modelData = new FormData();
    const inputs: {
      quantityId: string;
      modelName: string;
    }[] = [];
    const outputs: {
      quantityId: string;
      modelName: string;
      offsetExpected: number;
    }[] = [];

    let simulationBindingsFileName = "";
    for (const file of uploadedFiles.files) {
      // required main body properties: values (only first one)
      if (modelData.get("main") === null) {
        if (modelType.value === FileType.dymola && file.name === "dymosim")
          modelData.append("main", file);
        if (modelType.value !== FileType.dymola && isExpectedFormat(file, ["fmu"]))
          modelData.append("main", file);
      }

      if (modelData.get("variables") === null) {
        // required variables body properties: values
        if (modelType.value === FileType.dymola && file.name === "dsin.txt")
          modelData.append("variables", file);
        if (
          modelType.value !== FileType.dymola &&
          file.name.includes("iteration_variable") &&
          isExpectedFormat(file, ["csv"])
        )
          modelData.append("variables", file);
      }

      if (modelData.get("parameters") === null) {
        if (
          (modelType.value === FileType.fmuTf || modelType.value === FileType.fmuTo) &&
          file.name.includes("parameters") &&
          isExpectedFormat(file, ["csv"])
        ) {
          modelData.append("parameters", file);
        }
      }

      if (modelData.get("image") === null) {
        // optional image property: value (only first one)
        if (isExpectedFormat(file, ["svg", "png"])) modelData.append("image", file);
      }

      // optional binding property: value (only first one)

      if (
        !file.name.includes("iteration_variable") &&
        isExpectedFormat(file, ["csv"]) &&
        inputs.length === 0 &&
        outputs.length === 0
      ) {
        simulationBindingsFileName = file.name;
        const csv = await file.text();
        const parsedCsvArray = parseCSV(csv, "line", CSVColumnMapping);

        if (parsedCsvArray === undefined)
          throw new Error("Should not happen since the mapping has already been checked");

        for (const line of parsedCsvArray) {
          const quantity = quantities.find(q => q.name === line["name"]);

          /** Allow existing quantity to be model input */
          if (quantity) {
            const input = buildInputBinding(line, quantity?.id);
            if (input) inputs.push(input);
          }

          /** Allow existing quantity to be model input */
          if (quantity && line["outputModelName"]) {
            const offset = Number(line["offsetExpected"]);
            const offsetExpected = Number.isNaN(offset) ? 0 : offset;
            outputs.push({
              quantityId: quantity.id,
              modelName: line["outputModelName"],
              offsetExpected,
            });
          }
        }
      }
    }
    // if valid input and/or output in files

    if (inputs.length > 0) {
      await postInputs({
        body: { fileName: simulationBindingsFileName, inputs },
        routeParams: { projectId, setupId: setup.id },
      });
    }
    if (outputs.length > 0) {
      await postOutputs({
        body: { fileName: simulationBindingsFileName, outputs },
        routeParams: { projectId, setupId: setup.id },
      });
    }

    // main + variables and optional image property
    if (isModelType(modelType.value)) {
      const requestObject = {
        body: modelData,
        routeParams: { projectId, setupId: setup.id },
        query: { type: modelType.value },
      };
      await postModel(requestObject);
    } else {
      throw new Error("should not happen, value is always DYMOLA, FMU_T0 or FMU_TF type");
    }

    addToast("success", t("setup.simulation.import_batch.checking"));

    return true;
  }, [
    modelType.value,
    uploadedFiles.files,
    quantities,
    projectId,
    setup.id,
    postModel,
    addToast,
    postInputs,
    postOutputs,
  ]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={
        uploadedFiles.files.length < 1
          ? null
          : valid && uploadedFiles.files.length > 0
            ? handleSubmit
            : false
      }
      okButtonName={t("modalForm.button.name.import")}
    >
      <Select
        {...modelType}
        autoFocus
        options={modelTypesAllowed}
        onChange={resetState}
        clearable={false}
        prompt={t("setup.paragraphs.model.actions.import.modal.select.prompt")}
        label={t("setup.simulation.import_model.modal.subtitle")}
        disabled={settings.ALLOW_USE_DYMOSIM_AND_FMU_T0 === false}
      />
      {/* TO DO add logic for multiple plurial attribut */}
      <Dropzone onDrop={handleDrop} multiple />
      <ModalInfos
        modelType={modelType.value}
        submitted={submitted}
        validation={uploadedFiles.validations}
        invalid={submitted && valid === false}
      />
    </ModalForm>
  );
};

export default React.memo(ImportModelModalForm);
